
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ArticleDetail from "@/components/ArticleDetail";
import { getCurrentIssue } from "@/lib/data/issue/currentIssue";
import { getArticleById } from "@/lib/data";
import { Article } from "@/lib/types";
import { useAuth } from "@/contexts/AuthContext";
import { useArticleReads } from "@/hooks/useArticleReads";
import { useUserPreferences } from "@/hooks/useUserPreferences";

const ArticleView = () => {
  const { id } = useParams<{ id: string }>();
  const [currentIssue, setCurrentIssue] = useState<string>("May 2025");
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const { isRead, toggleReadState } = useArticleReads(id || "");
  const { preferences, loading: preferencesLoading } = useUserPreferences();

  
  useEffect(() => {
    const loadCurrentIssue = async () => {
      const issueData = await getCurrentIssue();
      if (issueData?.text) {
        setCurrentIssue(issueData.text);
      }
    };
    
    loadCurrentIssue();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchArticle = async () => {
      setLoading(true);
      setArticle(null);

      if (id) {
        try {
          const articleData = await getArticleById(id);

          if (cancelled) return;

          if (articleData) {
            setArticle(articleData);
            
            // Update page title using the article title
            document.title = `${articleData.title} | The Klatsch`;
            
            // Update or create Open Graph meta tags
            updateMetaTags(articleData);
          } else {
            document.title = "Article not found | The Klatsch";
          }
        } catch (error) {
          if (cancelled) return;
          console.error("Error fetching article for meta tags:", error);
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      } else {
        document.title = "Article not found | The Klatsch";
        setLoading(false);
      }
    };
    
    fetchArticle();
    
    // Clean up meta tags when unmounting, but don't change title here
    // Let the destination page handle its own title
    return () => {
      // Remove Open Graph meta tags
      const metaTags = document.querySelectorAll('meta[property^="og:"]');
      metaTags.forEach(tag => tag.remove());
      cancelled = true;
    };
  }, [id]);

  // Separate effect for auto-marking as read
  useEffect(() => {
    if (!preferencesLoading && id && article) {
      if (isAuthenticated && !isRead && preferences.auto_mark_read) {
        toggleReadState();
      }
    }
  }, [id, isAuthenticated, isRead, toggleReadState, preferences.auto_mark_read, preferencesLoading, article]);

  const updateMetaTags = (article: Article) => {
    // Helper function to create or update meta tags
    const setMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };
    
    // Get the full URL for the image
    const fullImageUrl = "https://kjfwyaniengzduyeeufq.supabase.co/storage/v1/object/public/logos/defaultImage.png";
    
    // Set Open Graph tags
    setMetaTag('og:title', article.title);
    setMetaTag('og:description', article.description);
    setMetaTag('og:type', 'article');
    setMetaTag('og:image', fullImageUrl);
    setMetaTag('og:url', window.location.href);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar currentIssue={currentIssue} />
      <main className="prose prose-lg max-w-none pb-12 markdown-content-container flex-grow overflow-y-auto">
        <ArticleDetail article={article} loading={loading} currentIssue={currentIssue} />
      </main>
      
      {/* Add styles to properly isolate markdown content and ensure titles render correctly */}
      <style>{`
        .markdown-content-container {
          isolation: isolate;
        }
        
        /* Fix heading margins in markdown content */
        .prose-headings h1, 
        .prose-headings h2, 
        .prose-headings h3, 
        .prose-headings h4, 
        .prose-headings h5, 
        .prose-headings h6 {
          margin: 0;
          line-height: 1.3;
        }
        
        /* Remove extra paragraph margins in markdown headings */
        .prose-headings p {
          margin: 0;
        }
        
        /* Ensure content is scrollable */
        html, body {
          height: 100%;
          overflow-y: auto;
        }
      `}</style>
    </div>
  );
};

export default ArticleView;
